import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Play, Pause, Plus, Trash2, Save, ArrowLeft,
    Crown, Download, Move, GripVertical,
    Volume2, VolumeX, SkipBack, SkipForward, Lock,
    RotateCcw, RotateCw, Bold, Italic // <--- Added Bold and Italic icons
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- CONSTANTS & HELPERS ---

const FONTS = [
    { value: 'Roboto', label: 'Roboto', family: 'font-roboto' },
    { value: 'Oswald', label: 'Oswald', family: 'font-heading' },
    { value: 'Poppins', label: 'Poppins', family: 'font-body' },
    { value: 'Bangers', label: 'Bangers', family: 'font-comic' },
    { value: 'Lora', label: 'Lora', family: 'font-lora' },
    { value: 'Courier Prime', label: 'Courier Prime', family: 'font-mono' },
    { value: 'Permanent Marker', label: 'Permanent Marker', family: 'font-handwritten' },
    { value: 'Anton', label: 'Anton', family: 'font-brand' },
    { value: 'Nunito', label: 'Nunito', family: 'font-nunito' },
    { value: 'Montserrat', label: 'Montserrat', family: 'font-montserrat' },
];

const PRESETS = [
    { value: 'standard', label: 'Standard', font: 'Roboto', color: '#FFFFFF', isPro: false },
    { value: 'meme', label: 'Meme', font: 'Oswald', color: '#FFFF00', isPro: true },
    { value: 'cinema', label: 'Cinema', font: 'Lora', color: '#FFFFFF', isPro: true },
];

const SIZE_MAP = {
    small: 'text-lg',
    medium: 'text-2xl',
    large: 'text-3xl',
    huge: 'text-4xl',
};

const formatTime = (seconds) => {
    const safeSeconds = Math.max(0, seconds);
    const mins = Math.floor(safeSeconds / 60);
    const secs = Math.floor(safeSeconds % 60);
    const ms = Math.floor((safeSeconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
};

const parseTime = (timeStr) => {
    const parts = timeStr.split(':');
    if (parts.length === 2) {
        const [mins, secsPart] = parts;
        const [secs, ms = '0'] = secsPart.split('.');
        const total = parseInt(mins) * 60 + parseInt(secs) + parseInt(ms) / 100;
        return isNaN(total) ? 0 : total;
    }
    return parseFloat(timeStr) || 0;
};

const resolveConflicts = (subs, videoDuration) => {
    let sorted = [...subs].sort((a, b) => a.start_time - b.start_time);

    for (let i = 0; i < sorted.length; i++) {
        const current = sorted[i];
        if (current.start_time < 0) current.start_time = 0;
        if (current.end_time - current.start_time < 0.5) {
            current.end_time = current.start_time + 0.5;
        }
        if (i > 0) {
            const prev = sorted[i - 1];
            if (current.start_time < prev.end_time) {
                const dur = current.end_time - current.start_time;
                current.start_time = prev.end_time;
                current.end_time = current.start_time + dur;
            }
        }
    }
    const last = sorted[sorted.length - 1];
    if (last && last.end_time > videoDuration) {
        last.end_time = videoDuration;
        if (last.end_time - last.start_time < 0.5) {
            last.start_time = Math.max(0, last.end_time - 0.5);
        }
    }
    return sorted;
};

// --- COMPONENTS ---
const TimeInput = ({ seconds, onUpdate, className, ...props }) => {
    const [localValue, setLocalValue] = useState(formatTime(seconds));
    useEffect(() => { setLocalValue(formatTime(seconds)); }, [seconds]);
    const handleBlur = () => {
        const time = parseTime(localValue);
        if (!isNaN(time) && time >= 0) { onUpdate(time); setLocalValue(formatTime(time)); }
        else { setLocalValue(formatTime(seconds)); }
    };
    return (
        <Input
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className={className}
            {...props}
        />
    );
};

// --- NEW COMPONENT: Rich Text Input ---
const RichTextInput = ({ value, onChange, className, id }) => {
    const editorRef = useRef(null);

    // Only update innerHTML from props if it differs from current content
    // This prevents the cursor from jumping to the start while typing
    useEffect(() => {
        if (editorRef.current && value !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = value || '';
        }
    }, [value]);

    const handleInput = (e) => {
        onChange(e.target.innerHTML);
    };

    return (
        <div
            id={id}
            ref={editorRef}
            contentEditable
            className={`p-3 min-h-[80px] overflow-y-auto ${className}`}
            onInput={handleInput}
            onBlur={handleInput}
            style={{ outline: 'none' }}
            data-placeholder="Enter subtitle text..."
        />
    );
};

// --- UPDATED COMPONENT: Format Toolbar ---
function FormatToolbar({ isPro }) {
    const savedRangeRef = useRef(null);

    useEffect(() => {
        const handleSelectionChange = () => {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                let node = range.commonAncestorContainer;
                let isInsideEditor = false;
                while (node) {
                    if (node.nodeType === 1 && node.getAttribute('contenteditable') === 'true') {
                        isInsideEditor = true;
                        break;
                    }
                    node = node.parentNode;
                }
                if (isInsideEditor) {
                    savedRangeRef.current = range;
                }
            }
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []);

    const handleFormat = (e, command, value = null) => {
        e.preventDefault();
        document.execCommand(command, false, value);
    };

    const handleDropdownFormat = (e, command) => {
        if (savedRangeRef.current) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(savedRangeRef.current);
        }
        document.execCommand(command, false, e.target.value);
        e.target.selectedIndex = 0; 
    };

    return (
        <div className={`flex flex-col lg:flex-row gap-2 p-1.5 bg-muted/40 border border-border rounded-md mb-2 relative ${!isPro ? 'opacity-60 overflow-hidden' : ''}`}>
            
            {/* PRO LOCK OVERLAY */}
            {!isPro && (
                <div 
                    className="absolute inset-0 z-10 flex items-center justify-center cursor-pointer bg-background/10 backdrop-blur-[1px]"
                    onClick={() => toast.error('Inline formatting is a Pro feature!')}
                    title="Upgrade to Pro"
                >
                    <div className="bg-background shadow-md border border-border rounded-full px-3 py-1 flex items-center gap-2">
                        <Lock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-[10px] font-heading uppercase text-muted-foreground tracking-wider">Pro Required</span>
                    </div>
                </div>
            )}

            {/* Group 1: Styles & Colors */}
            <div className="flex flex-wrap items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-background" 
                    onMouseDown={(e) => handleFormat(e, 'bold')} 
                    onTouchStart={(e) => handleFormat(e, 'bold')} title="Bold">
                    <Bold className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-background" 
                    onMouseDown={(e) => handleFormat(e, 'italic')} 
                    onTouchStart={(e) => handleFormat(e, 'italic')} title="Italic">
                    <Italic className="w-4 h-4" />
                </Button>
                
                <div className="w-[1px] h-5 bg-border mx-1" />
                
                <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-background" 
                    onMouseDown={(e) => handleFormat(e, 'foreColor', '#ef4444')} 
                    onTouchStart={(e) => handleFormat(e, 'foreColor', '#ef4444')} title="Red Text">
                    <div className="w-4 h-4 rounded-full bg-red-500" />
                </Button>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-background" 
                    onMouseDown={(e) => handleFormat(e, 'foreColor', '#eab308')} 
                    onTouchStart={(e) => handleFormat(e, 'foreColor', '#eab308')} title="Yellow Text">
                    <div className="w-4 h-4 rounded-full bg-yellow-500" />
                </Button>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-background" 
                    onMouseDown={(e) => handleFormat(e, 'foreColor', '#22c55e')} 
                    onTouchStart={(e) => handleFormat(e, 'foreColor', '#22c55e')} title="Green Text">
                    <div className="w-4 h-4 rounded-full bg-green-500" />
                </Button>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-background" 
                    onMouseDown={(e) => handleFormat(e, 'foreColor', '#06b6d4')} 
                    onTouchStart={(e) => handleFormat(e, 'foreColor', '#06b6d4')} title="Cyan Text">
                    <div className="w-4 h-4 rounded-full bg-cyan-500" />
                </Button>
            </div>

            {/* Group 2: Selectors & Clear Button */}
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto flex-1">
                <select 
                    className="h-8 text-xs bg-background border border-input rounded-md px-1 focus:outline-none cursor-pointer flex-1 min-w-[70px] lg:flex-none lg:w-24"
                    onChange={(e) => handleDropdownFormat(e, 'fontName')}
                    title="Change Font"
                >
                    <option value="" disabled selected>Font...</option>
                    {FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>

                <select 
                    className="h-8 text-xs bg-background border border-input rounded-md px-1 focus:outline-none cursor-pointer flex-1 min-w-[70px] lg:flex-none lg:w-20"
                    onChange={(e) => handleDropdownFormat(e, 'fontSize')}
                    title="Change Size"
                >
                    <option value="" disabled selected>Size...</option>
                    <option value="2">Small</option>
                    <option value="3">Normal</option>
                    <option value="5">Large</option>
                    <option value="7">Huge</option>
                </select>

                <Button variant="outline" size="sm" className="h-8 px-2 text-xs bg-background ml-auto" 
                    onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); document.execCommand('foreColor', false, '#ffffff'); }} 
                    onTouchStart={(e) => { e.preventDefault(); document.execCommand('removeFormat'); document.execCommand('foreColor', false, '#ffffff'); }}>
                    Clear
                </Button>
            </div>
        </div>
    );
}

// --- HOOKS ---
function useMediaQuery(query) {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        const media = window.matchMedia(query);
        if (media.matches !== matches) {
            setMatches(media.matches);
        }
        const listener = () => setMatches(media.matches);
        media.addEventListener('change', listener);
        return () => media.removeEventListener('change', listener);
    }, [matches, query]);
    return matches;
}


export default function Editor() {
    const { clipId } = useParams();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session');
    const navigate = useNavigate();
    const { isAuthenticated, isPro } = useAuth();

    // Detect Screen Size
    const isDesktop = useMediaQuery('(min-width: 768px)');

    // State
    const [clip, setClip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [title, setTitle] = useState('');
    const [subtitles, setSubtitles] = useState([]);
    const [selectedSubtitle, setSelectedSubtitle] = useState(null);
    const [stylePreset, setStylePreset] = useState('standard');
    const [customStyle, setCustomStyle] = useState({ font_family: 'Roboto', text_color: '#FFFFFF', font_size: 'medium' });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [errorSubtitleId, setErrorSubtitleId] = useState(null);

    // Player State
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [dragState, setDragState] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);
    const [mobilePanel, setMobilePanel] = useState('subtitles');

    // Refs
    const videoRef = useRef(null);
    const timelineRef = useRef(null);
    const mobileTimelineRef = useRef(null);
    const subtitlesRef = useRef([]);

    // Dialogs
    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [showLimitDialog, setShowLimitDialog] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

    useEffect(() => { subtitlesRef.current = subtitles; }, [subtitles]);

    // Data Fetching
    useEffect(() => {
        const fetchData = async () => {
            try {
                const clipResponse = await axios.get(`${API}/clips/${clipId}`);
                setClip(clipResponse.data);
                setDuration(clipResponse.data.duration);

                if (sessionId) {
                    const sessionResponse = await axios.get(`${API}/session/${sessionId}`);
                    setTitle(sessionResponse.data.title);
                    setSubtitles(sessionResponse.data.subtitle_data || []);
                    setStylePreset(sessionResponse.data.style_preset || 'standard');
                    if (sessionResponse.data.custom_style) setCustomStyle(sessionResponse.data.custom_style);
                } else {
                    const stashedWork = localStorage.getItem('subhack_wip_data');
                    if (stashedWork) {
                        try {
                            const parsed = JSON.parse(stashedWork);
                            setTitle(parsed.title || `Remix of ${clipResponse.data.title}`);
                            setSubtitles(parsed.subtitles || []);
                            setStylePreset(parsed.style_preset || 'standard');
                            if (parsed.custom_style) setCustomStyle(parsed.custom_style);
                            toast.success("Work restored! You can now save your project.");
                            localStorage.removeItem('subhack_wip_data');
                            localStorage.removeItem('subhack_return_url');
                        } catch (e) {
                            setTitle(`Remix of ${clipResponse.data.title}`);
                        }
                    } else {
                        setTitle(`Remix of ${clipResponse.data.title}`);
                    }
                }
                setHasUnsavedChanges(false);
            } catch (error) {
                toast.error('Failed to load clip');
                navigate('/gallery');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [clipId, sessionId, navigate]);

    const getActiveTimelineRect = () => {
        let rect = timelineRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) {
            rect = mobileTimelineRef.current?.getBoundingClientRect();
        }
        return rect;
    };

    // Drag Handlers
    const handleListDragStart = (e, id) => { e.dataTransfer.setData('text/plain', id); e.dataTransfer.effectAllowed = 'move'; };
    const handleListDragOver = (e, id) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropTargetId !== id) setDropTargetId(id); };
    const handleListDragLeave = () => { setDropTargetId(null); };
    const handleListDrop = (e, targetId) => {
        e.preventDefault();
        setDropTargetId(null);
        const sourceId = e.dataTransfer.getData('text/plain');
        if (sourceId === targetId) return;
        let newSubs = subtitles.map(s => ({ ...s }));
        newSubs.sort((a, b) => a.start_time - b.start_time);
        const sourceIndex = newSubs.findIndex(s => s.id === sourceId);
        const targetIndex = newSubs.findIndex(s => s.id === targetId);
        if (sourceIndex === -1 || targetIndex === -1) return;
        const sourceSub = newSubs[sourceIndex];
        const sourceDuration = sourceSub.end_time - sourceSub.start_time;
        const sourceStart = sourceSub.start_time;
        newSubs.splice(sourceIndex, 1);
        newSubs.forEach(sub => { if (sub.start_time > sourceStart) { sub.start_time -= sourceDuration; sub.end_time -= sourceDuration; } });
        let insertionIndex = targetIndex;
        let insertionTime = 0;
        if (insertionIndex > 0) insertionTime = newSubs[insertionIndex - 1].end_time;
        for (let i = insertionIndex; i < newSubs.length; i++) { newSubs[i].start_time += sourceDuration; newSubs[i].end_time += sourceDuration; }
        sourceSub.start_time = insertionTime;
        sourceSub.end_time = insertionTime + sourceDuration;
        newSubs.splice(insertionIndex, 0, sourceSub);
        setSubtitles(resolveConflicts(newSubs, duration));
        setHasUnsavedChanges(true);
    };

    const getEventPosition = (e) => {
        if (e.touches && e.touches.length > 0) return { clientX: e.touches[0].clientX };
        return { clientX: e.clientX };
    };

    const handleTimelineDragStart = (e, sub, type) => {
        e.stopPropagation();
        if (e.type === 'mousedown') e.preventDefault();
        if (e.type === 'touchstart') document.body.style.overflow = 'hidden';
        const pos = getEventPosition(e);
        setDragState({ type, id: sub.id, startX: pos.clientX, originalStart: sub.start_time, originalEnd: sub.end_time });
        setSelectedSubtitle(sub.id);
        if (videoRef.current && isPlaying) { videoRef.current.pause(); setIsPlaying(false); }
    };

    useEffect(() => {
        const handleMove = (e) => {
            if (!dragState) return;
            const rect = getActiveTimelineRect();
            if (!rect || rect.width === 0) return;

            const pos = getEventPosition(e);
            const deltaSeconds = ((pos.clientX - dragState.startX) / rect.width) * duration;

            setSubtitles(prev => prev.map(sub => {
                if (sub.id !== dragState.id) return sub;
                let newStart = sub.start_time, newEnd = sub.end_time;
                const minDur = 0.5;
                if (dragState.type === 'move') {
                    const blockDur = dragState.originalEnd - dragState.originalStart;
                    newStart = dragState.originalStart + deltaSeconds;
                    newEnd = newStart + blockDur;
                    if (newStart < 0) { newStart = 0; newEnd = blockDur; }
                    if (newEnd > duration) { newEnd = duration; newStart = duration - blockDur; }
                } else if (dragState.type === 'resize-l') {
                    newStart = Math.min(dragState.originalEnd - minDur, dragState.originalStart + deltaSeconds);
                    newStart = Math.max(0, newStart);
                } else if (dragState.type === 'resize-r') {
                    newEnd = Math.max(dragState.originalStart + minDur, dragState.originalEnd + deltaSeconds);
                    newEnd = Math.min(duration, newEnd);
                }
                if (videoRef.current) {
                    const previewTime = dragState.type === 'resize-r' ? newEnd : newStart;
                    if (Math.abs(videoRef.current.currentTime - previewTime) > 0.1) { videoRef.current.currentTime = previewTime; setCurrentTime(previewTime); }
                }
                return { ...sub, start_time: newStart, end_time: newEnd };
            }));
        };

        const handleEnd = () => {
            if (dragState) {
                setSubtitles(resolveConflicts(subtitlesRef.current, duration));
                setDragState(null);
                setHasUnsavedChanges(true);
                document.body.style.overflow = '';
            }
        };

        if (dragState) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', handleEnd);
            window.addEventListener('touchmove', handleMove, { passive: false });
            window.addEventListener('touchend', handleEnd);
        }
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
        };
    }, [dragState, duration]);

    const handleTimelineDoubleClick = (e) => {
        const rect = getActiveTimelineRect();
        if (!rect || rect.width === 0) return;
        const pos = getEventPosition(e);
        const clickTime = Math.max(0, Math.min(duration, ((pos.clientX - rect.left) / rect.width) * duration));
        const newSub = { id: `sub-${Date.now()}`, text: '', start_time: clickTime, end_time: Math.min(duration, clickTime + 2) };
        setSubtitles(resolveConflicts([...subtitles, newSub], duration));
        setSelectedSubtitle(newSub.id);
        setHasUnsavedChanges(true);
        if (videoRef.current) { videoRef.current.currentTime = clickTime; setCurrentTime(clickTime); }
    };

    const handleTimeUpdate = useCallback(() => {
        if (videoRef.current && !dragState) setCurrentTime(videoRef.current.currentTime);
    }, [dragState]);

    const handlePlayPause = useCallback(() => {
        if (videoRef.current) {
            if (isPlaying) videoRef.current.pause();
            else videoRef.current.play();
            setIsPlaying(!isPlaying);
        }
    }, [isPlaying]);

    const handleSeek = (e) => {
        if (dragState) return;
        const rect = getActiveTimelineRect();
        if (rect && rect.width > 0 && videoRef.current) {
            const pos = getEventPosition(e);
            const newTime = ((pos.clientX - rect.left) / rect.width) * duration;
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Check for inputs, textareas, OR our new contentEditable divs
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;

            if (e.code === 'Space') { e.preventDefault(); handlePlayPause(); }
            else if (e.code === 'ArrowLeft') { e.preventDefault(); if (videoRef.current) videoRef.current.currentTime = Math.max(0, currentTime - 1); }
            else if (e.code === 'ArrowRight') { e.preventDefault(); if (videoRef.current) videoRef.current.currentTime = Math.min(duration, currentTime + 1); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handlePlayPause, currentTime, duration]);

    const addSubtitle = () => {
        const newSub = { id: `sub-${Date.now()}`, text: '', start_time: currentTime, end_time: Math.min(currentTime + 2, duration) };
        setSubtitles(resolveConflicts([...subtitles, newSub], duration));
        setSelectedSubtitle(newSub.id);
        setHasUnsavedChanges(true);
    };

    const updateSubtitle = (id, updates) => {
        setSubtitles(prev => prev.map(sub => sub.id === id ? { ...sub, ...updates } : sub));
        setHasUnsavedChanges(true);
        if (errorSubtitleId === id) setErrorSubtitleId(null);
    };

    // --- NEW LOGIC: Inject Formatting Tags ---
    const applyFormat = (subId, openTag, closeTag) => {
        const textarea = document.getElementById(`textarea-${subId}`);
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentText = textarea.value;
        const selectedText = currentText.substring(start, end);

        const newText = currentText.substring(0, start) + openTag + selectedText + closeTag + currentText.substring(end);

        updateSubtitle(subId, { text: newText });

        // Push cursor to the correct spot after React re-renders the DOM
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + openTag.length, start + openTag.length + selectedText.length);
        }, 10);
    };

    const deleteSubtitle = (id) => {
        setSubtitles(subtitles.filter(sub => sub.id !== id));
        if (selectedSubtitle === id) setSelectedSubtitle(null);
        if (errorSubtitleId === id) setErrorSubtitleId(null);
        setHasUnsavedChanges(true);
    };

    const getCurrentSubtitle = () => subtitles.find(sub => currentTime >= sub.start_time && currentTime <= sub.end_time);

    const getSubtitleStyle = () => {
        const preset = PRESETS.find(p => p.value === stylePreset);
        const isCustom = stylePreset === 'custom';
        const fontFamily = isCustom ? customStyle.font_family : preset?.font || 'Roboto';
        const color = isCustom ? customStyle.text_color : preset?.color || '#FFFFFF';
        const size = isCustom ? SIZE_MAP[customStyle.font_size] : 'text-2xl';
        const fontClass = FONTS.find(f => f.value === fontFamily)?.family || 'font-roboto';
        return { fontClass, color, sizeClass: size };
    };

    const handleSave = async (isExiting = false) => {
        if (!isAuthenticated) {
            const wipData = {
                title,
                subtitles: resolveConflicts(subtitles, duration),
                style_preset: stylePreset,
                custom_style: customStyle
            };
            localStorage.setItem('subhack_wip_data', JSON.stringify(wipData));
            localStorage.setItem('subhack_return_url', window.location.pathname + window.location.search);
            toast.loading("Saving progress... Redirecting to Sign Up");
            navigate('/register');
            return false;
        }

        if (!isPro && !sessionId) {
            try {
                const response = await axios.get(`${API}/my-sessions`);
                if (response.data.filter(s => s.status === 'active').length >= 1) { setShowLimitDialog(true); return false; }
            } catch (error) { console.error('Failed check:', error); }
        }

        setSaving(true);
        try {
            const cleanSubs = resolveConflicts(subtitles, duration);
            const sanitizedSubs = cleanSubs.map(s => ({ ...s, start_time: Number(s.start_time.toFixed(3)), end_time: Number(s.end_time.toFixed(3)), text: s.text || "" }));
            const payload = { clip_id: clipId, title, subtitle_data: sanitizedSubs, style_preset: stylePreset, custom_style: isPro ? customStyle : null };

            if (sessionId) await axios.put(`${API}/session/${sessionId}`, payload);
            else { const response = await axios.post(`${API}/session`, payload); if (!isExiting) navigate(`/editor/${clipId}?session=${response.data.id}`, { replace: true }); }

            toast.success('Project saved!');
            setHasUnsavedChanges(false);
            return true;
        } catch (error) {
            if (error.response?.status === 520) {
                toast.success('Project saved!');
                setHasUnsavedChanges(false);
                return true;
            }

            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                const message = Array.isArray(detail) ? detail[0].msg : detail;

                const lineMatch = message.match(/subtitle line (\d+)/);
                if (lineMatch) {
                    const lineNum = parseInt(lineMatch[1], 10);
                    const cleanSubs = resolveConflicts(subtitles, duration);
                    const failingSub = cleanSubs[lineNum - 1];

                    if (failingSub) {
                        setErrorSubtitleId(failingSub.id);
                        setSelectedSubtitle(failingSub.id);
                        if (!isDesktop) setMobilePanel('subtitles');
                    }
                }
                toast.error(message);
            } else {
                toast.error('Failed to save');
            }
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => { if (hasUnsavedChanges) setShowUnsavedDialog(true); else navigate(-1); };
    const confirmSaveAndExit = async () => { const success = await handleSave(true); if (success) { setShowUnsavedDialog(false); navigate(-1); } };
    const confirmExitWithoutSave = () => { setShowUnsavedDialog(false); navigate(-1); };
    const downloadSRT = () => {
        if (!isPro) { toast.error('SRT download is a Pro feature'); return; }
        const cleanSubs = resolveConflicts(subtitles, duration);
        let srtContent = '';
        cleanSubs.forEach((sub, index) => {
            // Simple strip HTML for SRT file since SRT doesn't support complex CSS
            const strippedText = sub.text.replace(/<[^>]+>/g, '');
            srtContent += `${index + 1}\n00:${formatTime(sub.start_time).replace('.', ',')} --> 00:${formatTime(sub.end_time).replace('.', ',')}\n${strippedText}\n\n`;
        });
        const blob = new Blob([srtContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `${title}.srt`; a.click();
        URL.revokeObjectURL(url);
    };

    const setVideoRef = useCallback((node) => {
        videoRef.current = node;
        if (node && Math.abs(node.currentTime - currentTime) > 0.5) {
            node.currentTime = currentTime;
        }
    }, [currentTime]);

    if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Skeleton className="w-16 h-16 rounded-full" /></div>;

    const currentSub = getCurrentSubtitle();
    const subtitleStyle = getSubtitleStyle();

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <header className="h-14 border-b border-border flex items-center justify-between px-2 md:px-4 shrink-0">
                <div className="flex items-center gap-2 md:gap-4">
                    <Button variant="ghost" size="sm" onClick={handleBack} className="min-h-[44px] min-w-[44px] p-2 md:px-3">
                        <ArrowLeft className="w-4 h-4 md:mr-2" /><span className="hidden md:inline">Back</span>
                    </Button>
                    <Input value={title} onChange={(e) => { setTitle(e.target.value); setHasUnsavedChanges(true); }} className="w-28 md:w-64 h-8 bg-muted border-none text-sm font-heading" placeholder="Project title" />
                </div>
                <div className="flex items-center gap-2">
                    {isPro && <Button variant="outline" size="sm" onClick={downloadSRT} className="hidden md:flex min-h-[44px]"><Download className="w-4 h-4 mr-2" />.SRT</Button>}
                    <Button className="btn-primary h-10 md:h-8 min-h-[44px] px-3 md:px-4" onClick={() => handleSave(false)} disabled={saving}>
                        <Save className="w-4 h-4 md:mr-2" /><span className="hidden md:inline">{saving ? 'Saving...' : 'Save'}</span>
                    </Button>
                </div>
            </header>

            {isDesktop ? (
                <div className="grid grid-cols-12 flex-1 gap-2 p-2 overflow-hidden">
                    <div className="col-span-3 border border-border rounded-lg bg-card overflow-hidden flex flex-col">
                        <div className="p-3 border-b border-border flex items-center justify-between">
                            <h3 className="font-heading text-sm uppercase">Subtitles</h3>
                            <Button size="sm" variant="ghost" onClick={addSubtitle} className="min-h-[44px] min-w-[44px]"><Plus className="w-4 h-4" /></Button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 space-y-2">
                            {subtitles.map((sub) => (
                                <div key={sub.id} draggable onDragStart={(e) => handleListDragStart(e, sub.id)} onDragOver={(e) => handleListDragOver(e, sub.id)} onDragLeave={handleListDragLeave} onDrop={(e) => handleListDrop(e, sub.id)}
                                    className={`p-3 rounded-lg cursor-pointer transition-colors flex flex-col gap-1 border ${errorSubtitleId === sub.id ? 'border-red-500 bg-red-500/10' : dropTargetId === sub.id ? 'border-primary bg-primary/10' : selectedSubtitle === sub.id ? 'bg-primary/20 border-primary' : 'border-transparent bg-muted hover:bg-muted/80'}`}
                                    onClick={() => setSelectedSubtitle(sub.id)}>
                                    <div className="flex items-center gap-2">
                                        <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0" />
                                        {/* Strip HTML tags in the side list to keep it clean */}
                                        <p className="text-sm truncate flex-1">{sub.text.replace(/<[^>]+>/g, '')}</p>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-muted-foreground pl-6">
                                        <span className="font-mono">{formatTime(sub.start_time)} - {formatTime(sub.end_time)}</span>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive min-h-[44px] min-w-[44px]" onClick={(e) => { e.stopPropagation(); deleteSubtitle(sub.id); }}><Trash2 className="w-4 h-4" /></Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="col-span-6 flex flex-col gap-2">
                        <div className="flex-1 relative bg-black rounded-lg overflow-hidden">
                            <video
                                ref={setVideoRef}
                                src={clip?.video_url || ''}
                                className="w-full h-full object-contain cursor-pointer"
                                onClick={handlePlayPause}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={() => videoRef.current && setDuration(videoRef.current.duration)}
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                muted={isMuted}
                                playsInline
                            />
                            <div className="absolute inset-0 scanline-overlay opacity-20 pointer-events-none" />
                            {currentSub && (
                                <div className="absolute bottom-16 left-0 right-0 flex justify-center px-4 pointer-events-none">
                                    {/* --- RENDER HTML OVERLAY (DESKTOP) --- */}
                                    <span
                                        className={`${subtitleStyle.fontClass} ${subtitleStyle.sizeClass} text-center max-w-[90%]`}
                                        style={{ color: subtitleStyle.color, backgroundColor: 'rgba(0,0,0,0.7)', padding: '8px 12px', borderRadius: '6px', textShadow: '1px 1px 2px black' }}
                                        dangerouslySetInnerHTML={{ __html: currentSub.text }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="bg-card border border-border rounded-lg p-3 flex items-center gap-3">
                            <Button variant="ghost" size="sm" onClick={() => { if (videoRef.current) { videoRef.current.currentTime = 0; setCurrentTime(0); } }} className="min-h-[44px] min-w-[44px]">
                                <SkipBack className="w-5 h-5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => videoRef.current && (videoRef.current.currentTime -= 5)} className="min-h-[44px] min-w-[44px] relative">
                                <RotateCcw className="w-5 h-5" />
                                <span className="absolute text-[9px] font-bold mt-[1px]">5</span>
                            </Button>
                            <Button variant="ghost" size="icon" onClick={handlePlayPause} className="min-h-[44px] min-w-[44px]">{isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}</Button>
                            <Button variant="ghost" size="sm" onClick={() => videoRef.current && (videoRef.current.currentTime += 5)} className="min-h-[44px] min-w-[44px] relative">
                                <RotateCw className="w-5 h-5" />
                                <span className="absolute text-[9px] font-bold mt-[1px]">5</span>
                            </Button>

                            <span className="font-mono text-xs text-muted-foreground">{formatTime(currentTime)} / {formatTime(duration)}</span>
                            <div className="flex-1" />
                            <Button variant="ghost" size="sm" onClick={() => setIsMuted(!isMuted)} className="min-h-[44px] min-w-[44px]">{isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</Button>
                        </div>
                        <div ref={timelineRef} className="h-24 bg-card border border-border rounded-lg relative cursor-pointer select-none overflow-hidden touch-none" onClick={handleSeek} onDoubleClick={handleTimelineDoubleClick}>
                            <div className="absolute top-0 left-0 right-0 h-5 border-b border-border flex pointer-events-none">
                                {duration > 0 && [...Array(Math.ceil(duration / 5) + 1)].map((_, i) => <div key={i} className="text-xs text-muted-foreground font-mono absolute" style={{ left: `${(i * 5 / duration) * 100}%` }}>{formatTime(i * 5).split('.')[0]}</div>)}
                            </div>
                            <div className="absolute top-6 left-0 right-0 bottom-0 p-1">
                                {duration > 0 && subtitles.map((sub) => (
                                    <div key={sub.id} className={`absolute h-12 flex items-center px-1 rounded transition-colors group touch-none ${selectedSubtitle === sub.id ? 'bg-primary/30 border border-primary z-10' : 'bg-muted/80 border border-border hover:bg-muted z-0'}`}
                                        style={{ left: `${(sub.start_time / duration) * 100}%`, width: `${((sub.end_time - sub.start_time) / duration) * 100}%`, cursor: 'grab' }}
                                        onMouseDown={(e) => handleTimelineDragStart(e, sub, 'move')} onTouchStart={(e) => handleTimelineDragStart(e, sub, 'move')}>
                                        <div className="absolute left-0 top-0 bottom-0 w-3 cursor-w-resize hover:bg-primary/50 rounded-l" onMouseDown={(e) => handleTimelineDragStart(e, sub, 'resize-l')} onTouchStart={(e) => handleTimelineDragStart(e, sub, 'resize-l')} />
                                        <div className="flex-1 flex items-center justify-center overflow-hidden pointer-events-none"><GripVertical className="w-3 h-3 text-foreground/50 mr-1 shrink-0" /><span className="text-xs truncate">{sub.text.replace(/<[^>]+>/g, '')}</span></div>
                                        <div className="absolute right-0 top-0 bottom-0 w-3 cursor-w-resize hover:bg-primary/50 rounded-r" onMouseDown={(e) => handleTimelineDragStart(e, sub, 'resize-r')} onTouchStart={(e) => handleTimelineDragStart(e, sub, 'resize-r')} />
                                    </div>
                                ))}
                            </div>
                            <div className="absolute top-0 bottom-0 w-[2px] bg-red-500 z-50 pointer-events-none" style={{ left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }} />
                        </div>
                    </div>

                    <div className="col-span-3 border border-border rounded-lg bg-card overflow-hidden flex flex-col">
                        <Tabs defaultValue="edit" className="flex-1 flex flex-col">
                            <TabsList className="w-full rounded-none border-b border-border"><TabsTrigger value="edit" className="flex-1">Edit</TabsTrigger><TabsTrigger value="style" className="flex-1">Style</TabsTrigger></TabsList>
                            <TabsContent value="edit" className="flex-1 p-4 space-y-4 overflow-y-auto">
                                {selectedSubtitle ? (
                                    <>
                                        <div className="space-y-1">
                                                <Label className="font-heading text-xs uppercase text-muted-foreground block mb-1">Text</Label>
                                                <FormatToolbar isPro={isPro} />
                                                <RichTextInput
                                                id={`textarea-${selectedSubtitle}`}
                                                value={subtitles.find(s => s.id === selectedSubtitle)?.text || ''}
                                                onChange={(htmlText) => updateSubtitle(selectedSubtitle, { text: htmlText })}
                                                className={`input-base text-base font-sans leading-relaxed ${errorSubtitleId === selectedSubtitle ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-2"><Label className="font-heading text-xs uppercase">Start</Label><TimeInput seconds={subtitles.find(s => s.id === selectedSubtitle)?.start_time || 0} onUpdate={(time) => updateSubtitle(selectedSubtitle, { start_time: time })} className="input-base font-mono text-base" /></div>
                                            <div className="space-y-2"><Label className="font-heading text-xs uppercase">End</Label><TimeInput seconds={subtitles.find(s => s.id === selectedSubtitle)?.end_time || 0} onUpdate={(time) => updateSubtitle(selectedSubtitle, { end_time: time })} className="input-base font-mono text-base" /></div>
                                        </div>
                                    </>
                                ) : <div className="text-center py-8 text-muted-foreground text-sm"><Move className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>Select a subtitle to edit</p></div>}
                            </TabsContent>
                            <TabsContent value="style" className="flex-1 p-4 space-y-4 overflow-y-auto">
                                <StylePanel stylePreset={stylePreset} setStylePreset={setStylePreset} customStyle={customStyle} setCustomStyle={setCustomStyle} isPro={isPro} setHasUnsavedChanges={setHasUnsavedChanges} />
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col flex-1 overflow-hidden">
                    <div className="sticky top-0 z-10 bg-background border-b border-border shadow-md">

                        <div className="flex flex-col landscape:flex-row landscape:items-stretch">
                            <div className="p-2 pb-0 landscape:w-[65%] landscape:pr-1 landscape:pb-2">
                                <div className="relative bg-black rounded-lg overflow-hidden aspect-video landscape:h-full">
                                    <video
                                        ref={setVideoRef}
                                        src={clip?.video_url || ''}
                                        className="w-full h-full object-contain cursor-pointer"
                                        onClick={handlePlayPause}
                                        onTimeUpdate={handleTimeUpdate}
                                        onLoadedMetadata={() => videoRef.current && setDuration(videoRef.current.duration)}
                                        onPlay={() => setIsPlaying(true)}
                                        onPause={() => setIsPlaying(false)}
                                        muted={isMuted}
                                        playsInline
                                    />
                                    {currentSub && (
                                        <div className="absolute bottom-2 left-0 right-0 flex justify-center px-2 pointer-events-none">
                                            {/* --- RENDER HTML OVERLAY (MOBILE) --- */}
                                            <span
                                                className={`${subtitleStyle.fontClass} text-base text-center max-w-[95%]`}
                                                style={{
                                                    color: subtitleStyle.color,
                                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    textShadow: '1px 1px 2px black'
                                                }}
                                                dangerouslySetInnerHTML={{ __html: currentSub.text }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="px-2 py-1 landscape:w-[35%] landscape:pl-1 landscape:py-2 landscape:flex landscape:flex-col landscape:justify-center">
                                <div className="flex landscape:flex-wrap items-center justify-center gap-2 landscape:gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => { if (videoRef.current) { videoRef.current.currentTime = 0; setCurrentTime(0); } }} className="min-h-[44px] min-w-[44px] p-0">
                                        <SkipBack className="w-5 h-5" />
                                    </Button>

                                    <Button variant="ghost" size="sm" onClick={() => videoRef.current && (videoRef.current.currentTime -= 5)} className="min-h-[44px] min-w-[44px] p-0 relative">
                                        <RotateCcw className="w-5 h-5" />
                                        <span className="absolute text-[9px] font-bold mt-[1px]">5</span>
                                    </Button>

                                    <Button variant="ghost" size="icon" onClick={handlePlayPause} className="min-h-[48px] min-w-[48px]">
                                        {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7" />}
                                    </Button>

                                    <Button variant="ghost" size="sm" onClick={() => videoRef.current && (videoRef.current.currentTime += 5)} className="min-h-[44px] min-w-[44px] p-0 relative">
                                        <RotateCw className="w-5 h-5" />
                                        <span className="absolute text-[9px] font-bold mt-[1px]">5</span>
                                    </Button>

                                    <div className="flex-1 landscape:w-full landscape:text-center landscape:order-last">
                                        <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                                            {formatTime(currentTime)}
                                        </span>
                                    </div>

                                    <Button variant="ghost" size="sm" onClick={() => setIsMuted(!isMuted)} className="min-h-[44px] min-w-[44px] p-0">
                                        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="px-2 pb-2 pt-1 landscape:pt-0">
                            <div
                                ref={mobileTimelineRef}
                                className="h-16 bg-card border border-border rounded-lg relative cursor-pointer select-none overflow-hidden touch-none"
                                onClick={handleSeek}
                                onDoubleClick={handleTimelineDoubleClick}
                            >
                                <div className="absolute top-0 left-0 right-0 h-4 border-b border-border pointer-events-none">
                                    {duration > 0 && [...Array(Math.min(Math.ceil(duration / 10) + 1, 20))].map((_, i) => (
                                        <div key={i} className="text-[10px] text-muted-foreground font-mono absolute" style={{ left: `${(i * 10 / duration) * 100}%` }}>
                                            {Math.floor(i * 10 / 60)}:{((i * 10) % 60).toString().padStart(2, '0')}
                                        </div>
                                    ))}
                                </div>

                                <div className="absolute top-4 left-0 right-0 bottom-0 p-0.5">
                                    {duration > 0 && subtitles.map((sub) => (
                                        <div
                                            key={sub.id}
                                            className={`absolute h-full flex items-center justify-center px-0.5 rounded touch-none ${selectedSubtitle === sub.id ? 'bg-primary/40 border border-primary z-20' : 'bg-muted/80 border border-border z-10'}`}
                                            style={{
                                                left: `${(sub.start_time / duration) * 100}%`,
                                                width: `${((sub.end_time - sub.start_time) / duration) * 100}%`
                                            }}
                                            onTouchStart={(e) => handleTimelineDragStart(e, sub, 'move')}
                                            onMouseDown={(e) => handleTimelineDragStart(e, sub, 'move')}
                                        >
                                            <div
                                                className="absolute left-0 top-0 bottom-0 w-6 -ml-3 z-30 flex items-center justify-center"
                                                onTouchStart={(e) => handleTimelineDragStart(e, sub, 'resize-l')}
                                                onMouseDown={(e) => handleTimelineDragStart(e, sub, 'resize-l')}
                                            >
                                                {selectedSubtitle === sub.id && <div className="w-1 h-3 bg-primary/50 rounded-full" />}
                                            </div>

                                            <span className="text-[10px] truncate w-full text-center pointer-events-none select-none">
                                                {sub.text.replace(/<[^>]+>/g, '')}
                                            </span>

                                            <div
                                                className="absolute right-0 top-0 bottom-0 w-6 -mr-3 z-30 flex items-center justify-center"
                                                onTouchStart={(e) => handleTimelineDragStart(e, sub, 'resize-r')}
                                                onMouseDown={(e) => handleTimelineDragStart(e, sub, 'resize-r')}
                                            >
                                                {selectedSubtitle === sub.id && <div className="w-1 h-3 bg-primary/50 rounded-full" />}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div
                                    className="absolute top-0 bottom-0 w-[2px] bg-red-500 z-50 pointer-events-none"
                                    style={{ left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex border-b border-border bg-card">
                        <button
                            className={`flex-1 p-3 text-sm font-heading uppercase transition-colors ${mobilePanel === 'subtitles' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
                            onClick={() => setMobilePanel('subtitles')}
                        >
                            Subtitles
                        </button>
                        <button
                            className={`flex-1 p-3 text-sm font-heading uppercase transition-colors ${mobilePanel === 'style' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
                            onClick={() => setMobilePanel('style')}
                        >
                            Style
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto bg-background">
                        {mobilePanel === 'subtitles' && (
                            <div className="p-2 space-y-2 pb-20">
                                <div className="flex items-center justify-between p-2">
                                    <span className="text-sm text-muted-foreground">{subtitles.length} subtitle{subtitles.length !== 1 ? 's' : ''}</span>
                                    <Button size="sm" onClick={addSubtitle} className="min-h-[44px] gap-2"><Plus className="w-4 h-4" />Add</Button>
                                </div>
                                {subtitles.map((sub) => (
                                    <div
                                        key={sub.id}
                                        className={`p-3 rounded-lg border ${errorSubtitleId === sub.id ? 'border-red-500 bg-red-500/10' : selectedSubtitle === sub.id ? 'bg-primary/20 border-primary' : 'bg-muted border-transparent'}`}
                                        onClick={() => setSelectedSubtitle(sub.id)}
                                    >
                                        {selectedSubtitle === sub.id ? (
                                            <div className="space-y-1">
                                                <Label className="font-heading text-xs uppercase text-muted-foreground block mb-1">Text</Label>
                                                <FormatToolbar isPro={isPro} />
                                                <RichTextInput
                                                    id={`textarea-${sub.id}`}
                                                    value={sub.text}
                                                    onChange={(htmlText) => updateSubtitle(sub.id, { text: htmlText })}
                                                    className={`input-base text-base font-sans leading-relaxed ${errorSubtitleId === sub.id ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                                                />
                                                <div className="flex items-center gap-2">
                                                    <TimeInput seconds={sub.start_time} onUpdate={(t) => updateSubtitle(sub.id, { start_time: t })} className="input-base font-mono text-base md:text-xs flex-1 h-10" />
                                                    <span className="text-muted-foreground">-</span>
                                                    <TimeInput seconds={sub.end_time} onUpdate={(t) => updateSubtitle(sub.id, { end_time: t })} className="input-base font-mono text-base md:text-xs flex-1 h-10" />
                                                    <Button variant="ghost" size="sm" className="text-destructive min-h-[44px] min-w-[44px]" onClick={(e) => { e.stopPropagation(); deleteSubtitle(sub.id); }}><Trash2 className="w-4 h-4" /></Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-sm truncate">{sub.text.replace(/<[^>]+>/g, '')}</p>
                                                <span className="font-mono text-xs text-muted-foreground">{formatTime(sub.start_time)} - {formatTime(sub.end_time)}</span>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {mobilePanel === 'style' && (
                            <div className="p-4 space-y-4 pb-20">
                                <StylePanel stylePreset={stylePreset} setStylePreset={setStylePreset} customStyle={customStyle} setCustomStyle={setCustomStyle} isPro={isPro} setHasUnsavedChanges={setHasUnsavedChanges} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}><DialogContent><DialogHeader><DialogTitle>Sign Up</DialogTitle><DialogDescription>Create an account to save.</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => setShowAuthDialog(false)}>Cancel</Button><Button className="btn-primary" onClick={() => navigate('/register')}>Sign Up</Button></DialogFooter></DialogContent></Dialog>
            <Dialog open={showLimitDialog} onOpenChange={setShowLimitDialog}><DialogContent><DialogHeader><DialogTitle>Limit Reached</DialogTitle><DialogDescription>Upgrade to Pro for unlimited projects.</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => navigate('/dashboard')}>Dashboard</Button><Button className="btn-primary" onClick={() => navigate('/pricing')}>Upgrade</Button></DialogFooter></DialogContent></Dialog>
            <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}><DialogContent><DialogHeader><DialogTitle className="font-heading uppercase">Unsaved Changes</DialogTitle><DialogDescription>You have unsaved changes. Save before leaving?</DialogDescription></DialogHeader><DialogFooter className="gap-2 sm:gap-0"><Button variant="ghost" onClick={() => setShowUnsavedDialog(false)}>Cancel</Button><Button variant="outline" onClick={confirmExitWithoutSave} className="text-destructive hover:text-destructive">Don't Save</Button><Button className="btn-primary" onClick={confirmSaveAndExit}>Save & Exit</Button></DialogFooter></DialogContent></Dialog>
        </div>
    );
}

function StylePanel({ stylePreset, setStylePreset, customStyle, setCustomStyle, isPro, setHasUnsavedChanges }) {
    return (
        <>
            <div className="space-y-2">
                <Label className="font-heading text-xs uppercase">Preset</Label>
                <div className="grid grid-cols-1 gap-2">
                    {PRESETS.map((preset) => (
                        <button key={preset.value} className={`p-3 rounded-lg border text-left transition-colors min-h-[44px] ${stylePreset === preset.value ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'} ${preset.isPro && !isPro ? 'opacity-50' : ''}`}
                            onClick={() => { if (preset.isPro && !isPro) { toast.error('Pro required'); return; } setStylePreset(preset.value); setHasUnsavedChanges(true); }}>
                            <div className="flex items-center justify-between"><span className="font-heading text-sm">{preset.label}</span>{preset.isPro && <Crown className="w-4 h-4 text-primary" />}</div>
                            <div className="text-xs mt-1" style={{ color: preset.color, fontFamily: preset.font }}>Sample Text</div>
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between"><Label className="font-heading text-xs uppercase">Custom Style</Label>{!isPro && <Lock className="w-4 h-4 text-muted-foreground" />}</div>
                <div className={`space-y-4 ${!isPro ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="space-y-2">
                        <Label className="text-xs">Font</Label>
                        <Select value={customStyle.font_family} onValueChange={(v) => { setCustomStyle({ ...customStyle, font_family: v }); setStylePreset('custom'); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="input-base min-h-[44px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{FONTS.map((font) => <SelectItem key={font.value} value={font.value}><span style={{ fontFamily: font.value }}>{font.label}</span></SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs">Color</Label>
                        <div className="flex items-center gap-2">
                            <input type="color" value={customStyle.text_color} onChange={(e) => { setCustomStyle({ ...customStyle, text_color: e.target.value }); setStylePreset('custom'); setHasUnsavedChanges(true); }} className="w-12 h-12 rounded cursor-pointer border-none" />
                            <Input value={customStyle.text_color} onChange={(e) => { setCustomStyle({ ...customStyle, text_color: e.target.value }); setStylePreset('custom'); setHasUnsavedChanges(true); }} className="input-base font-mono text-sm" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs">Size: {customStyle.font_size}</Label>
                        <Slider value={[['small', 'medium', 'large', 'huge'].indexOf(customStyle.font_size)]} onValueChange={([v]) => { setCustomStyle({ ...customStyle, font_size: ['small', 'medium', 'large', 'huge'][v] }); setStylePreset('custom'); setHasUnsavedChanges(true); }} max={3} step={1} />
                    </div>
                </div>
            </div>
        </>
    );
}